import { formatCopy } from '@/i18n/ns';
import type { BillingStatus } from './api';
import {
  balanceCredits,
  credits,
  currentPlanIndex,
  planEndedAt,
  planState,
  shortDate,
  sortPlans,
} from './plan-math';
import { discountText, type BillingCopy } from './plan-view';

export { PAYMENT_FAILED } from './plan-math';

/** The catalogue plus a plan held outside it, so a demo plan is named like any other. */
const heldPlans = (status: BillingStatus) =>
  status.assignedPlan ? [...status.plans, status.assignedPlan] : status.plans;

const planName = (status: BillingStatus, id: string | null, copy: BillingCopy, locale: string) => {
  const plan = heldPlans(status).find((option) => option.id === id);
  return plan ? formatCopy(copy.planPerMonth, { credits: credits(plan.credits, locale) }) : null;
};

/** What the plan does next: renew, switch to another plan, or end. */
export function nextPlanText(status: BillingStatus, copy: BillingCopy, locale: string) {
  const account = status.account;
  if (!account.planActive || !account.periodEnd) return null;
  const date = shortDate(account.nextPlanAt ?? account.periodEnd, locale);
  if (account.cancelAtPeriodEnd) return formatCopy(copy.endsOn, { date });
  const next = account.nextPlanId && status.plans.find((plan) => plan.id === account.nextPlanId);
  if (next) return formatCopy(copy.switchesTo, { credits: credits(next.credits, locale), date });
  const plans = sortPlans(status.plans);
  const current = plans[currentPlanIndex(plans, account)] ?? status.assignedPlan;
  return current
    ? formatCopy(copy.renewsWith, { credits: credits(current.credits, locale), date })
    : null;
}

/** The plan line: the plan with its state when it is not active, or "No plan" when there never was one. */
export function planText(
  status: BillingStatus,
  copy: BillingCopy,
  locale: string,
  now = new Date(),
) {
  const account = status.account;
  const state = planState(account);
  const name = state === 'none' ? null : planName(status, account.planId, copy, locale);
  if (!name) return copy.noPlan;
  if (state === 'payment_failed') return `${name} · ${copy.planPaymentFailed}`;
  if (state === 'ended') {
    const ended = planEndedAt(account, now);
    return `${name} · ${ended ? formatCopy(copy.planEndedOn, { date: shortDate(ended, locale) }) : copy.planEnded}`;
  }
  return name;
}

export function PlanSummary({
  status,
  copy,
  locale,
}: {
  status: BillingStatus;
  copy: BillingCopy;
  locale: string;
}) {
  const account = status.account;
  const next = nextPlanText(status, copy, locale);
  const balance = balanceCredits(account.creditBalanceMicros);
  const discount = account.discount
    ? discountText({ ...account.discount, months: null, fresh: false }, copy, locale)
    : null;
  return (
    <dl className="company-details" data-testid="billing-plan-summary">
      <div>
        <dt>{copy.planLabel}</dt>
        <dd>{planText(status, copy, locale)}</dd>
      </div>
      <div>
        <dt>{copy.balanceLabel}</dt>
        <dd>{formatCopy(copy.creditsCount, { count: credits(balance, locale) })}</dd>
      </div>
      {next && (
        <div>
          <dt>{copy.nextLabel}</dt>
          <dd>{next}</dd>
        </div>
      )}
      {discount && (
        <div>
          <dt>{copy.discountLabel}</dt>
          <dd>{discount}</dd>
        </div>
      )}
    </dl>
  );
}
